import React, { useState, useEffect, createContext, useContext } from 'react';
import { getCurrentUser, signOut, signInWithRedirect } from 'aws-amplify/auth';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SUPPORTED_LANGUAGES, BEDROCK_MODELS } from '../utils/awsConfig';
import SystemPromptEditor from './SystemPromptEditor';

// Create context for AWS credentials
const CredentialsContext = createContext(null);

// Create context for settings
const SettingsContext = createContext(null);

export const useCredentials = () => {
  return useContext(CredentialsContext);
};

export const useSettings = () => {
  return useContext(SettingsContext);
};

const AuthWrapper = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  
  // Handle ESC key to close settings
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showSettings) {
        setShowSettings(false);
      }
    };
    
    if (showSettings) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showSettings]);
  
  // Settings state
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [selectedModel, setSelectedModel] = useState('nova-lite');
  const [customSystemPrompt, setCustomSystemPrompt] = useState('');
  const [realTimeMode, setRealTimeMode] = useState(true);
  const [showReasoning, setShowReasoning] = useState(false);

  // Model search state
  const [modelSearchOpen, setModelSearchOpen] = useState(false);
  const [modelSearchTerm, setModelSearchTerm] = useState('');

  // Filter models based on search term
  const filteredModels = Object.entries(BEDROCK_MODELS).filter(([key, model]) =>
    model.name.toLowerCase().includes(modelSearchTerm.toLowerCase()) ||
    model.provider.toLowerCase().includes(modelSearchTerm.toLowerCase())
  );

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      console.log('🔵 Checking auth state...');
      console.log('🔵 Current URL:', window.location.href);
      
      const currentUser = await getCurrentUser();
      console.log('🟢 User authenticated:', currentUser.username);
      setUser(currentUser);
      
      // Get AWS credentials for Bedrock access
      const session = await fetchAuthSession();
      if (session.credentials) {
        console.log('🟢 AWS credentials obtained');
        setCredentials({
          accessKeyId: session.credentials.accessKeyId,
          secretAccessKey: session.credentials.secretAccessKey,
          sessionToken: session.credentials.sessionToken
        });
      }
    } catch (error) {
      console.log('🔵 User not authenticated:', error.message);
      setUser(null);
      setCredentials(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      console.log('🔵 Sign in button clicked');
      console.log('🔵 Current URL:', window.location.href);
      console.log('🔵 Redirecting to Cognito Hosted UI...');
      
      setError(null);
      
      await signInWithRedirect({
        provider: 'Cognito'
      });
    } catch (error) {
      console.error('🔴 Sign in error:', error);
      setError(`Sign in failed: ${error.message}`);
    }
  };

  const handleSignOut = async () => {
    try {
      console.log('🔵 Signing out...');
      await signOut();
      setUser(null);
      setCredentials(null);
      // Redirect to home page after sign out
      window.location.href = '/';
    } catch (error) {
      console.error('🔴 Error signing out:', error);
      setError(`Sign out failed: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <h3>Loading...</h3>
          <p>Checking authentication status</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          padding: '20px'
        }}>
          <div style={{
            textAlign: 'center',
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <h1 style={{ 
              color: '#333', 
              marginBottom: '10px',
              fontSize: '2.5em'
            }}>
              Live Voice Translation
            </h1>
            <p style={{ 
              color: '#666', 
              marginBottom: '30px',
              fontSize: '1.1em'
            }}>
              Real-time voice translation powered by AWS Bedrock
            </p>

            {/* Error Display */}
            {error && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #f5c6cb'
              }}>
                <strong>Error:</strong> {error}
              </div>
            )}
            
            <button
              onClick={handleSignIn}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '15px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 10px rgba(0,123,255,0.3)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#0056b3';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#007bff';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              🚀 Sign In with auth.tanisorn.work
            </button>
            
            <p style={{ 
              color: '#888', 
              fontSize: '12px',
              marginTop: '20px'
            }}>
              You will be redirected to auth.tanisorn.work for secure authentication
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CredentialsContext.Provider value={credentials}>
      <SettingsContext.Provider value={{
        sourceLanguage, setSourceLanguage,
        targetLanguage, setTargetLanguage,
        selectedModel, setSelectedModel,
        customSystemPrompt, setCustomSystemPrompt,
        realTimeMode, setRealTimeMode,
        showReasoning, setShowReasoning
      }}>
        <div>
        {/* Header Info Bar */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'white',
          padding: '12px 0', // Remove horizontal padding from container
          display: 'flex',
          justifyContent: 'center', // Center the content
          alignItems: 'center',
          borderBottom: '1px solid #e9ecef',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '1200px', // Match App container maxWidth
            padding: '0 10px', // Match App container padding
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
          <div style={{ textAlign: 'left' }}>
            <h1 style={{ margin: '0 0 4px 0', color: '#333', fontSize: '1.4em', fontWeight: '600' }}>
              Live Voice Translation
            </h1>
            <p style={{ margin: '0 0 2px 0', color: '#666', fontSize: '0.8em' }}>
              Real-time voice translation powered by AWS Bedrock
            </p>
            <div style={{ margin: 0, color: '#888', fontSize: '0.85em', position: 'relative' }}>
              🤖 Using: 
              <div style={{ display: 'inline-block', position: 'relative', marginLeft: '8px' }}>
                <input
                  id="model-search"
                  name="model-search"
                  type="text"
                  value={modelSearchOpen ? modelSearchTerm : (BEDROCK_MODELS[selectedModel]?.name || selectedModel)}
                  onChange={(e) => setModelSearchTerm(e.target.value)}
                  onFocus={() => {
                    setModelSearchOpen(true);
                    setModelSearchTerm('');
                  }}
                  onBlur={() => {
                    setTimeout(() => setModelSearchOpen(false), 200);
                  }}
                  style={{
                    padding: '4px 20px 4px 8px',
                    fontSize: '0.85em',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#333',
                    cursor: 'pointer',
                    minWidth: '220px',
                    transition: 'all 0.2s ease',
                    boxShadow: modelSearchOpen ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                    borderColor: modelSearchOpen ? '#007bff' : '#ddd'
                  }}
                  placeholder="Type to search models..."
                />
                {/* Dropdown arrow indicator */}
                <div style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: `translateY(-50%) rotate(${modelSearchOpen ? '180deg' : '0deg'})`,
                  transition: 'transform 0.2s ease',
                  pointerEvents: 'none',
                  fontSize: '0.7em',
                  color: '#666'
                }}>
                  ▼
                </div>
                {modelSearchOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #007bff',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    fontSize: '0.85em',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    animation: 'slideDown 0.2s ease'
                  }}>
                    {filteredModels.map(([key, model]) => (
                      <div
                        key={key}
                        onClick={() => {
                          setSelectedModel(key);
                          setModelSearchOpen(false);
                          setModelSearchTerm('');
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          backgroundColor: selectedModel === key ? '#e3f2fd' : 'white',
                          transition: 'background-color 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = selectedModel === key ? '#e3f2fd' : 'white'}
                      >
                        <strong>{model.name}</strong> <span style={{color: '#666'}}>({model.provider})</span>
                      </div>
                    ))}
                    {filteredModels.length === 0 && (
                      <div style={{ padding: '8px 12px', color: '#666' }}>
                        No models found
                      </div>
                    )}
                  </div>
                )}
              </div>
              <style>{`
                @keyframes slideDown {
                  from {
                    opacity: 0;
                    transform: translateY(-10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}</style>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#495057',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.borderColor = '#adb5bd';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#dee2e6';
                }}
              >
                ⚙️ Settings
              </button>
              
              <button
                onClick={handleSignOut}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  border: '1px solid #dc3545',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#dc3545',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#dc3545';
                }}
              >
                🚪 Sign Out
              </button>
            </div>
            
            <p style={{ margin: 0, color: '#999', fontSize: '0.7em' }}>
              Made with 🫶 tanisorn.work
            </p>
          </div>
          </div> {/* Close the new container div */}
        </div>
        
        {/* Main App Content */}
        {children}

        {/* Settings Modal */}
        {showSettings && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowSettings(false)}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                position: 'relative',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSettings(false)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
              
              <h2 style={{ marginTop: 0, color: '#333' }}>⚙️ Settings</h2>
              
              {/* Language and Model Settings */}
              <div style={{ marginBottom: '25px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <SystemPromptEditor
                    customSystemPrompt={customSystemPrompt}
                    onSystemPromptChange={setCustomSystemPrompt}
                    sourceLanguage={sourceLanguage}
                    targetLanguage={targetLanguage}
                    supportedLanguages={SUPPORTED_LANGUAGES}
                    isRecording={false}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#555', fontSize: '16px' }}>Account Information</h3>
                <p style={{ color: '#666', margin: '5px 0' }}>
                  <strong>Email:</strong> {user?.signInDetails?.loginId || 'N/A'}
                </p>
                <p style={{ color: '#666', margin: '5px 0' }}>
                  <strong>User ID:</strong> {user?.userId || 'N/A'}
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#555', fontSize: '16px' }}>Application Info</h3>
                <p style={{ color: '#666', margin: '5px 0' }}>
                  <strong>Version:</strong> 250924.1630
                </p>
                <p style={{ color: '#666', margin: '5px 0' }}>
                  <strong>AWS Region:</strong> {process.env.REACT_APP_AWS_REGION || 'us-east-1'}
                </p>
              </div>

              <div style={{ textAlign: 'right', marginTop: '30px' }}>
                <button
                  onClick={() => setShowSettings(false)}
                  style={{
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </SettingsContext.Provider>
    </CredentialsContext.Provider>
  );
};

export default AuthWrapper;
